import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { Field, FormInput, FormSelect, FormSwitch, FormTextarea } from "@/components/jobOffer/ui";
import { SectionCard, SectionHeader, Grid2 } from "@/components/jobOffer/ui/layout";
import { CHANNEL_OPTIONS } from "@/components/campaign/channelOptions";

const CHANNEL_LABELS = Object.fromEntries(CHANNEL_OPTIONS.map((o) => [o.value, o.label]));
const POSITION_LABELS = ["Podstawowy", "Fallback 1", "Fallback 2"];

const ORDER_BUTTON_STYLE = {
    as: "button", type: "button", w: 7, h: 7, borderRadius: 6, display: "flex",
    alignItems: "center", justifyContent: "center", fontSize: "sm", bg: "bg.highlight",
    color: "text.white", border: "1px solid", borderColor: "border.primary",
};

const StepChannels = ({ values, onChange }) => {
    const cc = values.communicationChannels ?? {};
    const setChannels = (patch) => onChange({ ...values, communicationChannels: { ...cc, ...patch } });

    const channelOrder = cc.channelOrder?.length ? cc.channelOrder : CHANNEL_OPTIONS.map((o) => o.value);
    const moveChannel = (from, to) => {
        if (to < 0 || to >= channelOrder.length) return;
        const next = [...channelOrder];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        setChannels({ channelOrder: next });
    };

    const setWhatsApp = (patch) => setChannels({ whatsApp: { ...cc.whatsApp, ...patch } });
    const setSms = (patch) => setChannels({ sms: { ...cc.sms, ...patch } });
    const setEmail = (patch) => setChannels({ email: { ...cc.email, ...patch } });

    return (
        <VStack w="full" align="stretch" gap={4}>
            <SectionCard>
                <SectionHeader number={11} title="Kolejność i strategia kanałów" subtitle="W jakiej kolejności Agent ma próbować dotrzeć do leada" />
                <VStack align="stretch" gap={4}>
                    <VStack align="stretch" gap={2}>
                        {channelOrder.map((channel, i) => (
                            <HStack key={channel} borderWidth={1} borderColor="border.primary" borderRadius={8} p={3} justify="space-between">
                                <HStack gap={3}>
                                    <Box w={5} h={5} borderRadius="full" bg="purple.900" color="purple.400" fontSize="xs" display="flex" alignItems="center" justifyContent="center" fontWeight={500}>
                                        {i + 1}
                                    </Box>
                                    <Text fontSize="sm" color="text.white">{CHANNEL_LABELS[channel] ?? channel}</Text>
                                    <Text fontSize="10px" px={2} py={0.5} borderRadius={6} bg="bg.highlight" color="text.grey">
                                        {POSITION_LABELS[i] ?? `Fallback ${i}`}
                                    </Text>
                                </HStack>
                                <HStack gap={1}>
                                    <Box {...ORDER_BUTTON_STYLE} onClick={() => moveChannel(i, i - 1)} opacity={i === 0 ? 0.3 : 1} cursor={i === 0 ? "not-allowed" : "pointer"}>↑</Box>
                                    <Box {...ORDER_BUTTON_STYLE} onClick={() => moveChannel(i, i + 1)} opacity={i === channelOrder.length - 1 ? 0.3 : 1} cursor={i === channelOrder.length - 1 ? "not-allowed" : "pointer"}>↓</Box>
                                </HStack>
                            </HStack>
                        ))}
                    </VStack>
                    <Grid2>
                        <Field label="Czas opóźnienia między kanałami (min)">
                            <FormInput
                                type="number" min="0"
                                value={cc.delayBetweenChannelsMinutes ?? ""}
                                onChange={(e) => setChannels({ delayBetweenChannelsMinutes: e.target.value === "" ? null : Number(e.target.value) })}
                            />
                        </Field>
                    </Grid2>
                    <FormSwitch label="Komunikacja równoległa" checked={!!cc.parallelCommunication} onChange={(v) => setChannels({ parallelCommunication: v })} />
                </VStack>
            </SectionCard>

            <SectionCard>
                <SectionHeader number={12} title="WhatsApp" />
                <VStack align="stretch" gap={3}>
                    <FormSwitch label="Włącz WhatsApp" checked={!!cc.whatsApp?.enabled} onChange={(v) => setWhatsApp({ enabled: v })} />
                    <Grid2>
                        <Field label="Numer nadawcy (WhatsApp Business)">
                            <FormInput value={cc.whatsApp?.senderNumber ?? ""} onChange={(e) => setWhatsApp({ senderNumber: e.target.value })} />
                        </Field>
                        <Field label="Kanał fallback">
                            <FormSelect
                                value={cc.whatsApp?.fallbackChannel ?? ""}
                                onChange={(v) => setWhatsApp({ fallbackChannel: v || null })}
                                options={CHANNEL_OPTIONS.filter((o) => o.value !== "WhatsApp")}
                                placeholder="Brak"
                            />
                        </Field>
                    </Grid2>
                    <Field label="Wiadomość startowa WhatsApp" hint="Placeholdery: {imie}, {kampania}, {link}">
                        <FormTextarea value={cc.whatsApp?.startMessageTemplate ?? ""} onChange={(e) => setWhatsApp({ startMessageTemplate: e.target.value })} rows={3} />
                    </Field>
                    <Field label="Zasady ponownych prób (retry)">
                        <FormTextarea value={cc.whatsApp?.retryRules ?? ""} onChange={(e) => setWhatsApp({ retryRules: e.target.value })} rows={2} />
                    </Field>
                </VStack>
            </SectionCard>

            <SectionCard>
                <SectionHeader number={13} title="SMS" />
                <VStack align="stretch" gap={3}>
                    <FormSwitch label="Włącz SMS" checked={!!cc.sms?.enabled} onChange={(v) => setSms({ enabled: v })} />
                    <Grid2>
                        <Field label="Numer / ID nadawcy SMS">
                            <FormInput value={cc.sms?.senderIdOrNumber ?? ""} onChange={(e) => setSms({ senderIdOrNumber: e.target.value })} />
                        </Field>
                        <Field label="Fallback po SMS">
                            <FormSelect
                                value={cc.sms?.fallbackChannel ?? ""}
                                onChange={(v) => setSms({ fallbackChannel: v || null })}
                                options={CHANNEL_OPTIONS.filter((o) => o.value !== "Sms")}
                                placeholder="Brak"
                            />
                        </Field>
                    </Grid2>
                    <Field label="Szablon SMS" hint="Placeholdery: {imie}, {kampania}, {link}">
                        <FormTextarea value={cc.sms?.messageTemplate ?? ""} onChange={(e) => setSms({ messageTemplate: e.target.value })} rows={3} />
                    </Field>
                </VStack>
            </SectionCard>

            <SectionCard>
                <SectionHeader number={14} title="E-mail" />
                <VStack align="stretch" gap={3}>
                    <FormSwitch label="Włącz e-mail" checked={!!cc.email?.enabled} onChange={(v) => setEmail({ enabled: v })} />
                    <Grid2>
                        <Field label="Adres nadawcy">
                            <FormInput value={cc.email?.senderAddress ?? ""} onChange={(e) => setEmail({ senderAddress: e.target.value })} />
                        </Field>
                        <Field label="Nazwa nadawcy">
                            <FormInput value={cc.email?.senderName ?? ""} onChange={(e) => setEmail({ senderName: e.target.value })} />
                        </Field>
                    </Grid2>
                    <Field label="Temat wiadomości">
                        <FormInput value={cc.email?.subject ?? ""} onChange={(e) => setEmail({ subject: e.target.value })} />
                    </Field>
                    <Field label="Treść e-maila" hint="Placeholdery: {imie}, {kampania}, {link}">
                        <FormTextarea value={cc.email?.bodyTemplate ?? ""} onChange={(e) => setEmail({ bodyTemplate: e.target.value })} rows={4} />
                    </Field>
                    <Field label="Stopka e-maila">
                        <FormTextarea value={cc.email?.footer ?? ""} onChange={(e) => setEmail({ footer: e.target.value })} rows={2} />
                    </Field>
                    <Field label="Link do wersji WEB agenta">
                        <FormInput value={cc.email?.webAgentLink ?? ""} onChange={(e) => setEmail({ webAgentLink: e.target.value })} />
                    </Field>
                    <FormSwitch label="Możliwość dołączania załączników" checked={!!cc.email?.allowAttachments} onChange={(v) => setEmail({ allowAttachments: v })} />
                </VStack>
            </SectionCard>
        </VStack>
    );
};

export default StepChannels;
